---
title: "In and of itself"
subtitle: "a curious idiom"
date: 2026-03-18 00:32
---

Consider the idiom "in and of itself", as used in these sentences:

1. The statement was not, *in and of itself*, what caused the issue.
2. The first book of the trilogy is a complete story *in and of itself*.
3. Her watch told a story *in and of itself*.
4. You rarely look at a phone's screen *in and of itself*.
5. Pain exists *in and of itself*, and not merely in contrast to pleasure.

In all of these sentences it has roughly the same meaning, which is something like *considered on its own, without external reference*. And the nature of this external relationship varies based on its usage:

1. The statement caused some *external reason* which caused the issue.
2. The first book needs no *external support* to be a complete story.
3. Her watch needs no *external context* to tell a story.
4. You usually have an *external purpose* when you look at a phone's screen.
5. Pain exists outside of its *external relation* to pleasure.

To consider something "in and of itself", then, we consider how that thing can somehow reference itself:

1. The statement *wasn't its own reason* for causing the issue.
2. The first book *lends itself support* to be a complete story.
3. Her watch *established its own context* to tell a story.
4. A phone's screen rarely *gives itself purpose* to be looked at.
5. Pain can be *defined by referring to itself*.

This last one may seem strange, but consider this definition: *pain is anything that makes you to want to feel less of it*.

Now, consider the idiom "in and of itself", in and of itself. A literal, surface reading gives us two parts.

* What does it mean for something to be "in itself"? It could *include itself*, or be *within itself*, or be *part of itself*.

* What does it mean for something to be "of itself"? It could *originate from itself*, or be *comprised of itself*, or *belong to itself*.

A literal reading of "pain exists in and of itself" implies something like *pain contains itself* and *pain comes from itself*. If we are lax with our [referential distinctions,](/post/referential-distinctions) we might interpret this as something like:

* Our *concept* of pain contains, as part of it, our *experiences* of pain. It contains many other things as well, like our biological understanding of how pain works, or the memories we have of others being in pain.

* Our *experience* of pain comes from, at least partly, our *concept* of pain. This isn't to say that if we didn't know what pain is, we wouldn't experience it. Rather, our experience is affected and informed by our ideas of what pain is.

This generalizes: *anything* is in and of itself.
